/* STORY-003 — ask for a reset link.
 *
 * The whole screen exists to say the same thing in both cases. Somebody with an
 * account and somebody typing an address that has never been registered see an
 * identical confirmation, because anything else turns this form into a way of
 * discovering who is a customer.
 *
 * The confirmation is worded to admit that — "if there is an account for that
 * address" — rather than claiming a link definitely went out. A confident
 * promise would be a lie half the time, and the lie is what leaks the answer.
 */

import { requestPasswordResetRequestSchema } from '@eventuley/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useRequestPasswordReset } from '../api/index.js';

type FormValues = z.infer<typeof requestPasswordResetRequestSchema>;

export function ForgotPasswordScreen() {
  const t = useTranslate();
  const requestReset = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(requestPasswordResetRequestSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit((values) => {
    requestReset.mutate(values);
  });

  if (requestReset.isSuccess) {
    return (
      <AuthScreen title={t('auth.forgot.sent_title')} brand={t('app.name')}>
        <Callout tone="info">{t('auth.forgot.sent_body')}</Callout>
        <div className="auth__actions">
          <Link className="btn btn--ghost btn--block" to="/sign-in">
            {t('auth.forgot.back')}
          </Link>
        </div>
      </AuthScreen>
    );
  }

  const failure = requestReset.error;

  return (
    <AuthScreen
      title={t('auth.forgot.title')}
      brand={t('app.name')}
      subtitle={t('auth.forgot.body')}
    >
      {failure ? (
        <Callout
          tone={
            failure instanceof ApiError && failure.code === 'rate_limited' ? 'warning' : 'danger'
          }
        >
          {failure instanceof ApiError && failure.code === 'rate_limited'
            ? t('error.rate_limited')
            : failure instanceof NetworkError
              ? t('error.network')
              : t('error.unexpected')}
        </Callout>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('auth.forgot.email_label')}
          type="email"
          autoComplete="username"
          error={errors.email ? t('error.required') : undefined}
          {...register('email')}
        />

        <Button type="submit" block busy={isSubmitting || requestReset.isPending}>
          {isSubmitting || requestReset.isPending
            ? t('common.submitting')
            : t('auth.forgot.submit')}
        </Button>
      </form>

      <p className="auth__alt">
        <Link to="/sign-in">{t('auth.forgot.back')}</Link>
      </p>
    </AuthScreen>
  );
}
